import { NavLink, Outlet, useMatch } from 'react-router-dom';

import { ScreenTransition } from '@/components/ScreenTransition';
import { envIssues } from '@/config/env';
import { useIsAdmin } from '@/features/admin/useIsAdmin';
import { useArrivalEvents } from '@/features/analytics/useArrivalEvents';
import { useAuthSession } from '@/features/auth/useAuthSession';
import { useEnsureOnboarded } from '@/features/auth/useEnsureOnboarded';
import { useCreditsQuery } from '@/features/credits/useCreditsQuery';
import { TermsGate } from '@/features/legal/TermsGate';

const NAV_LINKS = [
  { to: '/', label: 'Главная', end: true },
  { to: '/history', label: 'История', end: false },
  { to: '/favorites', label: 'Избранное', end: false },
  { to: '/invite', label: 'Пригласить', end: false },
  { to: '/settings', label: 'Настройки', end: false },
];

/*
 * Пункт админки виден только администратору. Это удобство, а не защита:
 * прав он не даёт — каждую операцию проверяет сервер.
 */
const ADMIN_LINK = [{ to: '/admin', label: 'Админка', end: false }];

/**
 * Каркас приложения: навигация + текущий экран.
 *
 * Экраны ниже по дереву не проверяют isSupabaseConfigured сами — если
 * backend не настроен, дальше нечего показывать вообще, и AppShell
 * перехватывает это раньше, чем любой экран попробует сходить в Supabase.
 */
export function AppShell() {
  // Профиль и стартовые кредиты заводятся только после принятия документов.
  return (
    <TermsGate>
      <AppShellContent />
    </TermsGate>
  );
}

function AppShellContent() {
  const { status, error } = useAuthSession();
  const credits = useCreditsQuery();
  // На десктопе главная — витрина коллекций и занимает всю ширину каркаса;
  // остальные экраны сделаны под узкую колонку и в ней и остаются.
  const isHome = useMatch('/') !== null;

  // Отметки «пришёл по ссылке» и «вернулся из уведомления» ждут сессии,
  // поэтому живут в общем каркасе, а не на конкретном экране.
  useArrivalEvents();
  // Профиль и стартовые кредиты заводятся сразу после входа, а не при
  // первой генерации: иначе мастер блокирует создание первого портрета.
  useEnsureOnboarded();
  const isAdmin = useIsAdmin();

  if (status === 'unconfigured') {
    return (
      <main className="mx-auto flex min-h-dvh max-w-2xl flex-col justify-center gap-4 px-6 py-16">
        <h1 className="font-display text-h1 text-text">Supabase не настроен</h1>
        <p className="text-text-2">
          Скопируйте <code className="text-text">.env.example</code> в{' '}
          <code className="text-text">.env</code> и заполните переменные проекта Supabase.
        </p>
        <ul className="list-inside list-disc text-sm text-text-3">
          {envIssues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      </main>
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col lg:max-w-5xl">
      <nav
        aria-label="Основная навигация"
        className="flex flex-wrap items-center gap-x-4 border-b border-border px-6 py-1 text-sm"
      >
        {[...NAV_LINKS, ...(isAdmin ? ADMIN_LINK : [])].map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            className={({ isActive }) =>
              `inline-flex min-h-11 items-center ${
                isActive ? 'font-medium text-accent' : 'text-text-2 hover:text-text'
              }`
            }
          >
            {link.label}
          </NavLink>
        ))}

        {/*
          Баланс показывается только когда он действительно известен:
          прочерк или ноль «на всякий случай» ввели бы в заблуждение
          сильнее, чем его отсутствие.
        */}
        {credits.data !== undefined && (
          <NavLink
            to="/credits"
            aria-label="Осталось кредитов"
            className="ml-auto inline-flex min-h-11 items-center"
          >
            <span className="rounded-full border border-border px-3 py-1 text-xs text-text-2 tabular-nums">
              {credits.data}
            </span>
          </NavLink>
        )}
      </nav>

      {status === 'error' ? (
        <p className="px-6 py-8 text-danger">
          {error?.message ?? 'Не удалось войти. Обновите страницу.'}
        </p>
      ) : (
        <ScreenTransition className={isHome ? 'flex flex-1 flex-col' : 'mx-auto flex w-full max-w-2xl flex-1 flex-col'}>
          <Outlet />
        </ScreenTransition>
      )}
    </div>
  );
}
